import * as React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { KeyRound, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { EncodingSelect } from "@/components/crypto/EncodingSelect";
import { KeyIvFields } from "@/components/crypto/KeyIvFields";
import { ResultBox } from "@/components/crypto/ResultBox";
import { validateKeyIv } from "@/components/crypto/validation";
import { aesEncrypt, BYTE_ENCODINGS, OUTPUT_ENCODINGS } from "@/lib/crypto";

const schema = z
  .object({
    plaintext: z.string().min(1, "Enter the data you want to encrypt."),
    key: z.string().min(1, "Key is required."),
    keyEncoding: z.enum(BYTE_ENCODINGS),
    iv: z.string().min(1, "IV is required."),
    ivEncoding: z.enum(BYTE_ENCODINGS),
    outputEncoding: z.enum(OUTPUT_ENCODINGS),
  })
  .superRefine(validateKeyIv);

type EncryptValues = z.infer<typeof schema>;

export function EncryptForm() {
  const [result, setResult] = React.useState<string | null>(null);

  const form = useForm<EncryptValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      plaintext: "",
      key: "",
      keyEncoding: "utf-8",
      iv: "",
      ivEncoding: "utf-8",
      outputEncoding: "base64",
    },
  });

  async function onSubmit(values: EncryptValues) {
    try {
      const ciphertext = await aesEncrypt(values);
      setResult(ciphertext);
      toast.success("Data encrypted");
    } catch (e) {
      setResult(null);
      toast.error(e instanceof Error ? e.message : "Encryption failed");
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-xl">
          <Lock className="h-5 w-5" />
          Encrypt
        </CardTitle>
        <CardDescription>
          Enter a key, IV, and your data — AES-CBC returns the encrypted output.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="plaintext"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Data</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Text to encrypt…"
                      className="min-h-[96px] font-mono"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <KeyIvFields />

            <FormField
              control={form.control}
              name="outputEncoding"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between space-y-0">
                  <FormLabel>Output format</FormLabel>
                  <EncodingSelect
                    ariaLabel="Output format"
                    value={field.value}
                    onValueChange={field.onChange}
                    options={OUTPUT_ENCODINGS}
                  />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              className="w-full"
              disabled={form.formState.isSubmitting}
            >
              {form.formState.isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <KeyRound className="h-4 w-4" />
              )}
              Encrypt
            </Button>
          </form>
        </Form>

        {result !== null && (
          <ResultBox label="Encrypted data" value={result} className="mt-5" />
        )}
      </CardContent>
    </Card>
  );
}
